import React, { useState, useEffect } from 'react';
import { createQuote, createCustomer, fetchUsers } from '../api';
import { useNavigate } from 'react-router-dom';
import useDuplicateCustomerCheck from '../customers/useDuplicateCustomerCheck';
import customerLabel from '../customers/customerLabel';
import './quoteorder.css';

const NewQuote = ({ onQuoteCreated, onCancel }) => {
  const { confirmNewCustomer, duplicatePrompt } = useDuplicateCustomerCheck();
  const [formData, setFormData] = useState({
    customer: '',
    phone: '',
    vehicle: '',
    service_requested: 'Restoration',
    number_of_wheels: 4,
    tires: 'Yes',
    center_caps: 'Yes',
    description: '',
    notes: '',
    price: '',
    date_promised: '',
    time_promised: '',
    drop_off_date: '',
    drop_off_time: '',
    expiration_date: ''
  });

  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneValidity, setPhoneValidity] = useState({
    isValid: true,
    message: '',
  });
  const navigate = useNavigate();

  // Debounced customer search
  useEffect(() => {
    const searchCustomers = async () => {
      if (customerName.length < 2) {
        setCustomerSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetchUsers(customerName);
        setCustomerSearchResults(response.results || []);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [customerName]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setFormData(prev => ({
      ...prev,
      customer: customer.id,
      phone: customer.phone || '',
    }));
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'phone') {
      const pattern = /^[0-9]{10}$/;
      if (value.length > 0 && value.length < 10) {
        setPhoneValidity({
          isValid: false,
          message: `Please enter ${10 - value.length} more digits.`,
        });
      } else if (!pattern.test(value) && value.length >= 10) {
        setPhoneValidity({
          isValid: false,
          message: 'Invalid format. Please enter a 10-digit phone number.',
        });
      } else {
        setPhoneValidity({
          isValid: true,
          message: '',
        });
      }
    }
  };

  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setCustomerName(value);
    setSelectedCustomer(null);
    setShowDropdown(true);
    setFormData(prev => ({
      ...prev,
      customer: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!customerName.trim()) {
      setError('Customer name is required.');
      setLoading(false);
      return;
    }
    
    if (!formData.phone || !phoneValidity.isValid) {
      setError('A valid 10-digit phone number is required.');
      setLoading(false);
      return;
    }

    if (!formData.vehicle.trim()) {
      setError('Vehicle is required.');
      setLoading(false);
      return;
    }

    if (!formData.description.trim()) {
      setError('Description is required.');
      setLoading(false);
      return;
    }

    if (!formData.price.trim()) {
      setError('Price is required.');
      setLoading(false);
      return;
    }

    if (!formData.date_promised.trim()) {
      setError('Date promised is required.');
      setLoading(false);
      return;
    }

    try {
      let customerId;
      
      if (selectedCustomer) {
        // Use existing customer
        customerId = selectedCustomer.id;
      } else {
        const customerData = {
          name: customerName,
          phone: formData.phone
        };
        const choice = await confirmNewCustomer(customerData);
        if (!choice) return; // back to the form
        const customer = choice.action === 'use' ? choice.customer : await createCustomer(customerData);
        // Remember it, so retrying after a failed save doesn't ask (or create) again.
        setSelectedCustomer(customer);
        setCustomerName(customer.name);
        customerId = customer.id;
      }

      if (customerId) {
        const quoteData = {
          customer: customerId,
          phone: formData.phone,
          vehicle: formData.vehicle,
          service_requested: formData.service_requested,
          number_of_wheels: parseInt(formData.number_of_wheels),
          tires: formData.tires,
          center_caps: formData.center_caps,
          description: formData.description,
          notes: formData.notes || '',
          price: parseFloat(formData.price),
          date_promised: formData.date_promised,
          time_promised: formData.time_promised || '',
          drop_off_date: formData.drop_off_date || '',
          drop_off_time: formData.drop_off_time || '',
          expiration_date: formData.expiration_date || ''
        };
        
        const response = await createQuote(quoteData);
        if (onQuoteCreated) {
          onQuoteCreated(response);
        }
      }
    } catch (err) {
      setError('Failed to create quote. Please try again.');
      console.error('Error creating quote:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = () => {
    navigate('/accounts');
  };


  return (
    <form className='quoteorder' onSubmit={handleSubmit}>
      <div className="quoteorder-header-container">
        <h2 className='quoteorderheader'>Create New Quote</h2>
        <button type="button" className="close-button" onClick={onCancel}>×</button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className='quoteorderform'>
        <div className="quoteordertop">
          <div className='quoteordercontact'>
            <div className='quoteordercontactcolumn'>
              {/* Customer Name with Dropdown */}
              <div className="customer-search-container">
                <label className='quoteorderlabelcontact'>
                  Customer:
                  <input
                    className='quoteorderinputscontact'
                    type="text"
                    name="customerName"
                    value={customerName}
                    onChange={handleCustomerNameChange}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    placeholder="Search or enter new customer"
                    required
                  />
                </label>
                {showDropdown && customerName.length >= 2 && (
                  <div className="customer-dropdown">
                    {isSearching ? (
                      <div className="customer-dropdown-item">Searching...</div>
                    ) : customerSearchResults.length > 0 ? (
                      customerSearchResults.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-dropdown-item"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customerLabel(customer)}
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item">No customers found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Phone */}
              <label className='quoteorderlabelcontact'>
                Phone:
                <input
                  className={`quoteorderinputscontact ${!phoneValidity.isValid ? 'invalid' : ''}`}
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  pattern="[0-9]{10}"
                  placeholder="1234567890"
                  required
                />
                {!phoneValidity.isValid && <span className="quoteorderphoneErrorStyle">{phoneValidity.message}</span>}
              </label>
            </div>
            {/* Vehicle */}
            <label className='quoteorderlabelcontact'>
              Vehicle:
              <input
                className='quoteorderinputscontact'
                type="text"
                name="vehicle"
                value={formData.vehicle}
                onChange={handleInputChange}
                required
              />
            </label>
          </div>
          
          <div className='quoteorderimportantinfo'>
            {/* Price */}
            <label className='quoteorderlabelimportantinfo'>
              Price:
              <input
                className='quoteorderinputsprice'
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
              />
            </label>
            <div>
              {/* Expiration Date - positioned first */}
              <label className='quoteorderlabelimportantinfo'>
                Expiration Date:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="expiration_date"
                  value={formData.expiration_date}
                  onChange={handleInputChange}
                />
              </label>
            </div>
            <div>
              {/* Date and Time Promised - grouped together */}
              <label className='quoteorderlabelimportantinfo'>
                Date Promised:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="date_promised"
                  value={formData.date_promised}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label className='quoteorderlabelimportantinfo'>
                Time Promised:
                <input
                  className='quoteorderinputsprice'
                  type="time"
                  name="time_promised"
                  value={formData.time_promised}
                  onChange={handleInputChange}
                />
              </label>
            </div>
            <div>
              {/* Drop Off Date and Time - grouped together */}
              <label className='quoteorderlabelimportantinfo'>
                Drop Off Date:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="drop_off_date"
                  value={formData.drop_off_date}
                  onChange={handleInputChange}
                />
              </label>
              <label className='quoteorderlabelimportantinfo'>
                Drop Off Time:
                <input
                  className='quoteorderinputsprice'
                  type="time"
                  name="drop_off_time"
                  value={formData.drop_off_time}
                  onChange={handleInputChange}
                />
              </label>
            </div>
          </div>
        </div>
        <div className='quoteorderdetails'>
          <div className="quoteorderdetailssmall">
            {/* Service Requested */}
            <label className='quoteorderlabeldetails'>
              Service Requested:
              <select
                className='quoteorderinputsdetails'
                name="service_requested"
                value={formData.service_requested}
                onChange={handleInputChange}
                required
              >
                <option value="Restoration">Restoration</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Straightening">Straightening</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>

            {/* Number of Wheels/Parts */}
            <label className='quoteorderlabeldetails'>
              Number of Wheels/Parts:
              <input
                className='quoteorderinputsdetails'
                type="number"
                name="number_of_wheels"
                value={formData.number_of_wheels}
                onChange={handleInputChange}
                min="1"
                required
              />
            </label>

            {/* Tires */}
            <label className='quoteorderlabeldetails'>
              Tires:
              <select
                className='quoteorderinputsdetails'
                name="tires"
                value={formData.tires}
                onChange={handleInputChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="New">New</option>
              </select>
            </label>

            {/* Center Caps */}
            <label className='quoteorderlabeldetails'>
              Center Caps:
              <select
                className='quoteorderinputsdetails'
                name="center_caps"
                value={formData.center_caps}
                onChange={handleInputChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>

          <div className='quoteorderdetailslarge'>
            {/* Description */}
            <label className='quoteorderlabeldetailslarge'>
              Description:
              <textarea
                className='quoteorderinputsdetailslarge'
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </label>

            {/* Notes */}
            <label className='quoteorderlabeldetailslarge'>
              Notes:
              <textarea
                className='quoteorderinputsdetailslarge'
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
              />
            </label>
          </div>
        </div>
      </div>
      <div className='quoteordersavebuttonbanner'>
        {/* Desktop buttons */}
        <div className='desktop-buttons'>
          <button className='quoteordersavebutton' type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Quote'}
          </button>
        </div>

        {/* Mobile buttons */}
        <div className="mobile-buttons">
          <button className='mobile-save-button' type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Quote'}
          </button>
        </div>
      </div>
      {duplicatePrompt}
    </form>
  );
};

export default NewQuote; 